// src/services/topazService.js
class TopazService {
  constructor(apiKey) {
    this.apiKey = apiKey;
    // Use your Python backend as proxy instead of calling Topaz directly
    const base = (
      process.env.REACT_APP_PARSER_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      ''
    ).replace(/\/$/, '');
    this.baseURL = base; // Your Render backend URL
  }

  async enhanceImage(file, options = {}) {
    const {
      model = 'Standard V2',
      scale = 2,
      sharpen = 0.5,
      denoise = 0.3,
      fixCompression = true
    } = options;

    try {
      // Call your backend proxy instead of Topaz directly
      const formData = new FormData();
      formData.append('image', file);
      formData.append('model', model);
      formData.append('scale', scale.toString());
      formData.append('sharpen', sharpen.toString());
      formData.append('denoise', denoise.toString());
      formData.append('fix_compression', fixCompression.toString());

      const response = await fetch(`${this.baseURL}/enhance-image`, {
        method: 'POST',
        body: formData
        // No API key header needed - backend handles it
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Backend proxy error: ${response.status} - ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      
      return {
        jobId: result.id || Date.now(),
        downloadUrl: result.downloadUrl || result.output?.downloadUrl,
        status: 'completed',
        credits: result.creditsUsed || 1
      };

    } catch (error) {
      console.error('Topaz enhancement error:', error);
      throw new Error(`Enhancement failed: ${error.message}`);
    }
  }

  async createEnhanceJob(file, options) {
    // Try different possible endpoint structures
    const endpoints = [
      '/v1/image/enhance',
      '/image/enhance', 
      '/enhance',
      '/v1/enhance'
    ];

    for (const endpoint of endpoints) {
      try {
        console.log(`Trying endpoint: ${this.baseURL}${endpoint}`);
        
        const formData = new FormData();
        
        // Add the image file
        formData.append('image', file);
        
        // Add enhancement parameters
        formData.append('model', options.model);
        formData.append('scale', options.scale.toString());
        formData.append('sharpen', options.sharpen.toString());
        formData.append('denoise', options.denoise.toString());
        formData.append('fix_compression', options.fixCompression.toString());

        const response = await fetch(`${this.baseURL}${endpoint}`, {
          method: 'POST',
          headers: {
            'X-API-Key': this.apiKey,
          },
          body: formData
        });

        if (response.ok) {
          console.log(`Success with endpoint: ${endpoint}`);
          return await response.json();
        } else if (response.status !== 404) {
          // If it's not a 404, it might be the right endpoint with a different error
          const errorData = await response.json().catch(() => ({}));
          throw new Error(`API Error: ${response.status} - ${errorData.error || response.statusText}`);
        }
        
      } catch (error) {
        if (error.message.includes('404')) {
          continue; // Try next endpoint
        }
        throw error;
      }
    }

    throw new Error('No working API endpoint found. Please check Topaz Labs API documentation for current endpoints.');
  }

  async uploadFile(file, uploadUrl) {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} - ${response.statusText}`);
    }

    return response;
  }

  async startProcessing(jobId) {
    const response = await fetch(`${this.baseURL}/image/${jobId}/process`, {
      method: 'PATCH',
      headers: {
        'X-API-Key': this.apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action: 'start' })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Processing start failed: ${response.status} - ${errorData.error || response.statusText}`);
    }

    return await response.json();
  }

  async pollForCompletion(jobId, maxAttempts = 60, interval = 5000) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const status = await this.getJobStatus(jobId);
        
        if (status.state === 'completed') {
          return status;
        }
        
        if (status.state === 'failed') {
          throw new Error(`Processing failed: ${status.error || 'Unknown error'}`);
        }
        
        // Still processing, wait before next check
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, interval));
        }
        
      } catch (error) {
        if (attempt === maxAttempts - 1) {
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }
    
    throw new Error('Processing timeout - job did not complete in expected time');
  }

  async getJobStatus(jobId) {
    const response = await fetch(`${this.baseURL}/image/${jobId}/status`, {
      method: 'GET',
      headers: {
        'X-API-Key': this.apiKey,
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Status check failed: ${response.status} - ${errorData.error || response.statusText}`);
    }

    return await response.json();
  }

  async cancelJob(jobId) {
    const response = await fetch(`${this.baseURL}/image/${jobId}`, {
      method: 'DELETE',
      headers: {
        'X-API-Key': this.apiKey,
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Cancel failed: ${response.status} - ${errorData.error || response.statusText}`);
    }

    return await response.json();
  }

  async getCreditsRemaining() {
    try {
      const response = await fetch(`${this.baseURL}/user/credits`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.apiKey,
        }
      });

      if (!response.ok) {
        // If credits endpoint doesn't exist, return default
        return { remaining: 320 };
      }

      const data = await response.json();
      return data;
      
    } catch (error) {
      console.warn('Could not fetch credits:', error.message);
      return { remaining: 320 }; // Default fallback
    }
  }

  // Enhanced synchronous method for faster processing
  async enhanceImageSync(file, options = {}) {
    const {
      model = 'Standard V2',
      scale = 2,
      sharpen = 0.5,
      denoise = 0.3,
      fixCompression = true
    } = options;

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('model', model);
      formData.append('scale', scale.toString());
      formData.append('sharpen', sharpen.toString());
      formData.append('denoise', denoise.toString());
      formData.append('fix_compression', fixCompression.toString());

      const response = await fetch(`${this.baseURL}/image/enhance/sync`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.apiKey,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Sync enhancement failed: ${response.status} - ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      
      return {
        jobId: result.id,
        downloadUrl: result.output?.downloadUrl || result.downloadUrl,
        status: 'completed',
        credits: result.creditsUsed || 1
      };

    } catch (error) {
      console.error('Topaz sync enhancement error:', error);
      throw new Error(`Sync enhancement failed: ${error.message}`);
    }
  }

  // Estimate cost for bulk operations
  async estimateCredits(files, options = {}) {
    try {
      const estimateData = {
        count: files.length,
        model: options.model || 'Standard V2',
        scale: options.scale || 2
      };

      const response = await fetch(`${this.baseURL}/image/estimate`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(estimateData)
      });

      if (!response.ok) {
        // Fallback to simple calculation
        return { creditsRequired: files.length };
      }

      return await response.json();
      
    } catch (error) {
      console.warn('Could not estimate credits:', error.message);
      return { creditsRequired: files.length };
    }
  }

  // Health check - use backend proxy
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/topaz-health`, {
        method: 'GET'
        // No API key needed - backend handles it
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`Health check successful via backend proxy:`, result);
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Health check failed:', errorData);
        return false;
      }
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  // Get available models
  getAvailableModels() {
    return [
      {
        id: 'Standard V2',
        name: 'Standard V2',
        description: 'General-purpose model balancing detail, sharpness, and noise reduction',
        maxScale: 8,
        recommended: true
      },
      {
        id: 'Low Resolution V2',
        name: 'Low Resolution V2',
        description: 'Enhances clarity and detail in low-resolution images like web graphics',
        maxScale: 8,
        bestFor: ['screenshots', 'web graphics', 'low-res images']
      },
      {
        id: 'High Fidelity V2',
        name: 'High Fidelity V2',
        description: 'Preserves intricate details in professional photography',
        maxScale: 6,
        bestFor: ['photography', 'portraits', 'detailed images']
      },
      {
        id: 'CGI',
        name: 'CGI',
        description: 'Optimized for computer-generated illustrations',
        maxScale: 8,
        bestFor: ['digital art', 'illustrations', 'CGI renders']
      },
      {
        id: 'Text Refine',
        name: 'Text Refine',
        description: 'Designed for images with text and shapes',
        maxScale: 4,
        bestFor: ['documents', 'text images', 'diagrams']
      }
    ];
  }
}

export default TopazService;